import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import _ from 'lodash';
import { browserHistory } from 'react-router';

import AccountNumberAutoSuggests from '../common/accountNumberAutoSuggestsAvailPricing';
import {
	addToPartsCart,
	getOnlinePricingForParts,
	resetAvailabilityToCart,
	resetPricesToCart,
	saveAvailabilityToCart,
	saveCreateOrderPayload,
	saveOrderabilityToCart,
	savePricesToCart,
	saveToCart,
	updateCartOrderPoints
} from '../../../redux/actions';
import { DATE_FORMAT, DATE_TIME_FORMAT } from '../../common/messageConstants';
import GenericOverlayDialog from '../../dialogs/genericOverlayDialog';
import useDebounce from '../../../hooks/useDebounce';
import QuantityInput from '../common/quantityInput';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import {
	INTERNATIONAL_ZIP_CODE,
	MODIFIERS_EXCLUDED_FROM_VOLUME_DISCOUNT,
	PNET_APP_QUERY_PARAM,
	PNET_GOP_BATCH_SIZE,
	POK_ENABLED_SUBCLASSES,
	shippingMethods
} from '../../common/partsNetConstants';
import DatePicker from 'react-datepicker';
import { routes } from '../../../redux/api/routes';

const CustomerReferenceInput = ({ value, onChange }) => {
	const [customerReference, setCustomerReference] = useState(value.toUpperCase());
	const debouncedCustomerReference = useDebounce(customerReference);

	useUpdateEffect(() => {
		onChange(debouncedCustomerReference);
	}, [debouncedCustomerReference]);

	const handleChange = event => {
		setCustomerReference(event.target.value.toUpperCase());
	};

	return <input type="text" className="short" value={customerReference} onChange={handleChange} />;
};

const PartsNetCart = props => {
	const [itemsToAdd, setItemsToAdd] = useState([]);
	const [showSingleShipmentLimitDialog, setShowSingleShipmentLimitDialog] = useState(false);
	const { user, cart, order } = useSelector(({ user, cart, order }) => ({ user, cart, order }));
	const [requestedQuantityErr, setrequestedQuantityErr] = useState(false);
	let { availability, orderability, prices, cartPimData } = cart;

	let pimData = null;
	if (cartPimData) {
		pimData = cartPimData.pimData;
	}

	const cartData = cart.cart;
	const pricingData = cart.prices;
	const { orderType, requestedShipDate, shipMethod, shipSet, freightForwarder: freightForwarderString } = (cart.cart && cart.cart.responseObject) || {};
	const [showFreightForwarder, setShowFreightForwarder] = useState(() => (freightForwarderString ? 'Y' : 'N'));
	const [isFreightForwarderError, setIsFreightForwarderError] = useState(false);

	const apiCallsRef = useRef({
		isCartPimDataCallCompleted: false,
		isCartPricingCallCompleted: false
	});
	const staleAvailabilityDataRef = useRef(false);

	const dispatch = useDispatch();

	const hasPricingAccess = props.isResponsibilityExists('Price');
	const hasCreateOrderAccess = props.isResponsibilityExists('Create New Order');

	const refreshData = ({ pim, orderability, pricing } = {}) => {
		const items = [];

		cart.cart.responseObject &&
			cart.cart.responseObject.cartItems &&
			cart.cart.responseObject.cartItems.length > 0 &&
			cart.cart.responseObject.cartItems.forEach((cartItem, index) => {
				items.push({
					id: cartItem.buyerLineNo,
					itemNumber: cartItem.item,
					requestedQuantity: cartItem.quantity,
					requestedShipDate: moment(cart.cart.responseObject.requestedShipDate, DATE_FORMAT)
				});
			});

		if (items.length) {
			props.getItemDetails(
				{
					items,
					shipSet: cart.cart.responseObject.shipSet || undefined
				},
				data => {
					apiCallsRef.current.isCartPimDataCallCompleted = true;
					apiCallsRef.current.isCartPricingCallCompleted = true;
					const orderabilityDataForCartStore = {
						responseObject: []
					};
					const availabilityDataForCartStore = {
						responseObject: []
					};
					const pricingDataForCartStore = {
						responseObject: {
							items: []
						}
					};
					const pimDataForCartStore = {
						pimData: []
					};

					data.items.forEach(item => {
						if (pim) {
							pimDataForCartStore.pimData.push(item.pimData);
						}
						if (pricing) {
							pricingDataForCartStore.responseObject.items.push(item.pricingData);
						}
						if (
							orderability &&
							item.orderabilityData &&
							!orderabilityDataForCartStore.responseObject.some(orderabilityItem => orderabilityItem.INVENTORY_ITEM === item.itemNumber)
						) {
							orderabilityDataForCartStore.responseObject.push(item.orderabilityData);
						}
						if (item.availabilityData) {
							availabilityDataForCartStore.responseObject.push(item.availabilityData);
						}
					});

					if (pim) {
						dispatch(updateCartOrderPoints(pimDataForCartStore));
					}
					if (pricing) {
						dispatch(savePricesToCart(pricingDataForCartStore, true));
					}
					if (orderability) {
						dispatch(saveOrderabilityToCart(orderabilityDataForCartStore));
					}
					dispatch(saveAvailabilityToCart(availabilityDataForCartStore));
				},
				{
					searchContext: 'PNET',
					include: {
						pim,
						orderability,
						availability: true,
						pricing
					},
					parallelCalls: true,
					skipPOKCheck: true,
					showLoader: true
				}
			);
		}
	};

	const updateCart = (payload, callback) => {
		const {
			selectedAccount: { ACCOUNTNUMBER: accountNumber },
			userDetails
		} = user;

		const cartPayload = {
			...payload,
			accountNumber,
			userId: userDetails.sso,
			createdDate: moment().format(DATE_FORMAT),
			updatedDate: moment().format(DATE_FORMAT)
		};

		dispatch(
			addToPartsCart(cartPayload, cartResponse => {
				if (cartResponse.status && cartResponse.responseObject) {
					dispatch(saveToCart(cartResponse));
					callback && callback();
				} else {
					browserHistory.push('/error');
				}
			})
		);
	};

	useEffect(() => {
		if (showFreightForwarder === 'Y' && user.customerShipToDetailsAddressList) {
			if (!(order.createOrderPayload && order.createOrderPayload.availableFreightForwarders)) {
				props.fetchFreightForwarders(availableFreightForwarders => {
					dispatch(saveCreateOrderPayload({ ...(order.createOrderPayload || {}), availableFreightForwarders }));
				});
			}
		}
	}, [showFreightForwarder, user.customerShipToDetailsAddressList]);

	useEffect(() => {
		let cartPayload = cart.cart && cart.cart.responseObject;

		if (requestedShipDate && user.selectedAddress) {
			//update requested date to sysdate if requested date < sysdate
			if (moment(requestedShipDate, DATE_FORMAT) < moment(moment().format(DATE_FORMAT), DATE_FORMAT)) {
				cartPayload = {
					...cartPayload,
					requestedShipDate: moment().format(DATE_FORMAT)
				};
			} else {
				if (!props.isAnyAddressCallInProgress) {
					const selectedAddress = JSON.parse(user.selectedAddress);
					if (!order.createOrderPayload || (order.createOrderPayload && order.createOrderPayload.shipToSiteUseId !== selectedAddress.SITE_USE_ID)) {
						dispatch(saveCreateOrderPayload({ ...(order.createOrderPayload || {}), shipToSiteUseId: selectedAddress.SITE_USE_ID }));
						refreshData({
							pim: !apiCallsRef.current.isCartPimDataCallCompleted,
							availability: true,
							orderability: true,
							pricing: !apiCallsRef.current.isCartPricingCallCompleted
						});
					} else {
						refreshData({
							availability: true,
							orderability:
								!(orderability && orderability.length) ||
								(cart.cart.responseObject &&
									cart.cart.responseObject.cartItems &&
									cart.cart.responseObject.cartItems.some(cartItem => !orderability.some(orderabilityItem => orderabilityItem.INVENTORY_ITEM === cartItem.item)))
						});
					}
				}
			}
		}

		if (cart.cart && cartPayload !== cart.cart.responseObject) {
			updateCart(cartPayload);
		}
	}, [requestedShipDate, shipSet, user.selectedAddress, props.isAnyAddressCallInProgress]);

	const cartItems = cart.cart && cart.cart.responseObject && cart.cart.responseObject.cartItems;

	useEffect(() => {
		if (shipSet === '1' && staleAvailabilityDataRef.current) {
			staleAvailabilityDataRef.current = false;
			refreshData({ availability: true });
		}
	}, [cartItems]);

	const isPOKEnabled = useMemo(() => {
		if (props.isGEAPartsSalesAgreementSelected() && POK_ENABLED_SUBCLASSES.includes(user.selectedPriceSource['Sub Account Classification'])) {
			return true;
		}
		return false;
	}, [user.selectedPriceSource]);

	const handleRequestedShipDateChange = date => {
		let cartPayload = cart.cart.responseObject;
		cartPayload = {
			...cartPayload,
			requestedShipDate: date.format('MM/DD/YYYY')
		};

		updateCart(cartPayload);
	};

	const handleOrderDetailsChange = e => {
		const { name: fieldName, value: fieldValue } = e.target;
		let cartPayload;

		if (fieldName === 'shipSet' && fieldValue === '1' && cart.cart.responseObject.cartItems.length > PNET_GOP_BATCH_SIZE) {
			handleDialog('SingleShipmentLimitDialog', true);
			return;
		}

		if (fieldName === 'showFreightForwarder') {
			setShowFreightForwarder(fieldValue);
			setIsFreightForwarderError(false);
			if (fieldValue === 'N' && cart.cart.responseObject.freightForwarder) {
				cartPayload = {
					...cart.cart.responseObject,
					freightForwarder: null
				};
			}
		} else {
			cartPayload = {
				...cart.cart.responseObject,
				[fieldName]: fieldValue
			};

			if (fieldName === 'orderType') {
				cartPayload.shipMethod =
					shippingMethods[props.isInternationalPartsCustomer ? 'international' : 'domestic'][fieldValue === 'R' ? 'regular' : 'emergency'][0].displayName;
			} else if (fieldName === 'freightForwarder') {
				cartPayload = {
					...cartPayload,
					[fieldName]: `${fieldValue}|${
						order.createOrderPayload.availableFreightForwarders.find(availableFreightForwarder => availableFreightForwarder.SITE_USE_ID.toString() === fieldValue).NAME
					}`
				};

				if (isFreightForwarderError) {
					setIsFreightForwarderError(false);
				}
			}
		}

		if (cartPayload) {
			updateCart(cartPayload);
		}
	};

	const compareSalesAgreements = (priceRecord, item) => {
		return user.isDpmAccount
			? priceRecord.saNumber &&
					(priceRecord.saNumber.toString() === item.agreementNumber || priceRecord.saNumber.toString() === routes.dpmData.fallbackPartsSalesAgreementNumber)
			: priceRecord.saNumber && priceRecord.saNumber.toString() === item.agreementNumber;
	};

	const handleCreateOrder = () => {
		const items = [];

		cart.cart.responseObject &&
			cart.cart.responseObject.cartItems &&
			cart.cart.responseObject.cartItems.length > 0 &&
			cart.cart.responseObject.cartItems.forEach(cartItem => {
				items.push({
					id: cartItem.buyerLineNo,
					itemNumber: cartItem.item,
					itemDescription: cartItem.itemDescription,
					originalItemNumber: cartItem.parentItem,
					requestedQuantity: cartItem.quantity,
					agreementNumber: cartItem.agreementNumber,
					requestedShipDate: moment(cart.cart.responseObject.requestedShipDate, DATE_FORMAT),
					customerReference: cartItem.poNumber
				});
			});

		if (items.length) {
			props.getItemDetails(
				{
					items,
					shipSet: cart.cart.responseObject.shipSet || undefined
				},
				data => {
					const availabilityDataForCartStore = {
						responseObject: []
					};

					data.items.forEach(item => {
						availabilityDataForCartStore.responseObject.push(item.availabilityData);
					});

					dispatch(saveAvailabilityToCart(availabilityDataForCartStore));

					const { orderType, requestedShipDate, shipMethod, shipSet } = cart.cart.responseObject;
					const finalItems = [];

					const {
						selectedPriceSource: { 'Sub Account Classification': subAccountClassification },
						selectedAddress: selectedAddressString,
						selectedAccount
					} = user;
					const accountNumber = selectedAccount.ACCOUNTNUMBER;
					const selectedAddress = JSON.parse(selectedAddressString || '{}');
					const zipCode = selectedAddress.POSTALCODE && selectedAddress.POSTALCODE.split('-')[0];
					const billToAddress = props.getAddressByCategory('BILL_TO');

					let shipToSiteNumber = selectedAddress.SITENUMBER;
					let shipToAccountNumber = selectedAddress.SHIP_TO_ACCOUNT_NUMBER || accountNumber;
					if (!shipToSiteNumber) {
						const shipToAddress = props.getAddressByCategory('SHIP_TO');

						shipToSiteNumber = shipToAddress && shipToAddress.SITENUMBER;
						if (shipToAddress.SHIP_TO_ACCOUNT_NUMBER) {
							shipToAccountNumber = shipToAddress.SHIP_TO_ACCOUNT_NUMBER;
						}
					}

					const agreementNumber = user.selectedPriceSource.AGREEMENT_NUMBER;
					const shipMethodDetails = shippingMethods[props.isInternationalPartsCustomer ? 'international' : 'domestic'][orderType === 'R' ? 'regular' : 'emergency'].find(
						shippingMethodItem => shippingMethodItem.displayName === shipMethod
					);

					//for now it's one request and one sales agreement for all items in an order
					const pricingPayload = {
						priceRequest: {
							priceRequestType: 'PARTS ONLY',
							headerPricingDate: moment().format('DD-MMM-YYYY'),
							customerPartyAccountNumber: accountNumber,
							orderLevelModInd: 'Y',
							orderTotal: cartTotalForPayload,
							shipToState: selectedAddress.STATE,
							orderType: 'Standard Sales',
							orderReference: props.isInternationalPartsCustomer ? 'INTERNATIONAL ORDER' : 'CUSTOMER ORDER',
							shipToZipcode: zipCode,
							custClass: subAccountClassification.slice(-3),
							requestedDeliveryDate: moment(requestedShipDate).format('DD-MMM-YYYY'),
							source: 'PartsNet',
							serviceLevel: shipMethodDetails.displayName,
							shipmentCompCode: `${orderType}${shipSet === '1' ? 'T' : 'Z'}-${shipMethodDetails.code}`,
							shipmentOrderType: orderType,
							headerShipmentMethod: shipMethodDetails.ebsShipMethodName,
							shipmentPriorityCode: `${orderType}${shipSet === '1' ? 'T' : 'Z'}`,
							billToPartySite: billToAddress && billToAddress.SITE_USE_ID,
							shipToPartySite: selectedAddress.SITE_USE_ID,
							lineItems: []
						}
					};

					data.items.forEach(item => {
						item.pricingData = pricingData.find(
							pricingItem =>
								pricingItem.item_number === item.itemNumber && pricingItem.records.some(pricingItemRecord => compareSalesAgreements(pricingItemRecord, item))
						);
						item.pimData = pimData.find(pimItem => pimItem.item_number === item.itemNumber);

						if (
							item.pimData &&
							(!isPOKEnabled ||
								item.pimData.geego_pok_install !== 'Y' ||
								(item.requestedQuantity && item.requestedQuantity % item.pimData.geego_pok_ord_qty === 0)) &&
							item.availabilityData &&
							item.availabilityData.sourceOrganizationID &&
							item.availabilityData.sourceOrganizationCode &&
							![80].includes(item.availabilityData.errorCode) &&
							item.pricingData &&
							item.pricingData.records &&
							item.pricingData.records.length &&
							item.pricingData.records[0].unitPrice
						) {
							let isVolumeDiscountEligible = false;
							if (item.pimData && item.pimData.geego_discount_ind === 'Y') {
								if (item.pricingData.records.some(pricingRecord => MODIFIERS_EXCLUDED_FROM_VOLUME_DISCOUNT.includes(pricingRecord.modifierPriceSubtype))) {
									isVolumeDiscountEligible = false;
								} else {
									isVolumeDiscountEligible = true;
								}
							}
							item.isVolumeDiscountEligible = isVolumeDiscountEligible;

							finalItems.push(item);
							pricingPayload.priceRequest.lineItems.push({
								lineNo: pricingPayload.priceRequest.lineItems.length + 1,
								lineType: 'PART',
								item: item.itemNumber,
								qty: item.requestedQuantity,
								// "linePricingDate": null,
								salesAgreementNumber: agreementNumber.toString(),
								// "shipmentMethod": null,
								warehouseOrgCode: item.availabilityData.sourceOrganizationCode.split(':').at(-1),
								// "unCratedInd": null,
								// "grossPointValue": null,
								netItemIndicator: item.isVolumeDiscountEligible ? 'N' : 'Y',
								oversizePart: 'N'
							});
						}
					});

					// make pricing service call
					dispatch(
						getOnlinePricingForParts(pricingPayload, `requestType=priceOrder&${PNET_APP_QUERY_PARAM}`, onlinePricingResponse => {
							if (onlinePricingResponse.status && onlinePricingResponse.responseObject) {
								const orderInfo = {
									orderType,
									requestedShipDate: moment(requestedShipDate, DATE_FORMAT).format('YYYY-MM-DD'),
									shippingMethod: shipMethod,
									shipSet,
									freightForwarder:
										cart.cart.responseObject.freightForwarder && order.createOrderPayload && order.createOrderPayload.availableFreightForwarders
											? order.createOrderPayload.availableFreightForwarders.find(
													availableFreightForwarder =>
														availableFreightForwarder.SITE_USE_ID.toString() === cart.cart.responseObject.freightForwarder.split('|')[0]
											  )
											: null
								};

								const itemsForCheckout = [];
								finalItems.forEach((item, index) => {
									const onlinePricingData =
										onlinePricingResponse.responseObject &&
										onlinePricingResponse.responseObject.priceResponse &&
										onlinePricingResponse.responseObject.priceResponse.find(
											pricingItem => pricingItem.item === item.itemNumber && pricingItem.lineIndex.toString() === (index + 1).toString()
										);

									if (onlinePricingData) {
										const bagItems = [];
										(onlinePricingData.discounts || []).forEach(discount => {
											if (discount.bagItem && !bagItems.some(existing => existing.name === discount.bagItem)) {
												const bagItemLine = onlinePricingResponse.responseObject.priceResponse.find(
													line => line.item === null && (line.discounts || []).some(d => d.itemName === discount.bagItem)
												);
												 const bagItemQty = discount.parentLineQty || (bagItemLine ? bagItemLine.qty : 1);
                								 const bagItemPrice = bagItemLine ? bagItemLine.salesPrice : 0;

												bagItems.push({
													name: discount.bagItem,
													priceSubType: discount.priceSubType,
													price: bagItemPrice,
													quantity: bagItemQty,
													extendedPrice: bagItemPrice * bagItemQty
												});
											}
										});
										const newItem = {
											id: item.id,
											itemNumber: item.itemNumber,
											originalItemNumber: item.originalItemNumber,
											itemDescription: item.itemDescription,
											primaryUnitOfMeasure: item.pimData.primary_unit_of_measure,
											itemType: item.itemType,
											quantity: item.requestedQuantity,
											quantityError: '',
											availableQuantity: item.availabilityData.availableQuantity,
											warehouseCode: item.availabilityData.sourceOrganizationCode.split(':').at(-1),
											sourceOrganizationId: item.availabilityData.sourceOrganizationID,
											eta: item.availabilityData.arrivalDate,
											unitPrice: item.pricingData.records[0].unitPrice,
											adjustedUnitPrice: onlinePricingData.salesPrice,
											extendedPrice: onlinePricingData.extendedPrice,
											isVolumeDiscountEligible: item.isVolumeDiscountEligible,
											agreementNumber: item.agreementNumber,
											customerReference: item.customerReference,
											redirectFrom: 'cart',
											bagItems
										};

										itemsForCheckout.push(newItem);
									}
								});

								dispatch(
									saveCreateOrderPayload({
										...(order.createOrderPayload || {}),
										...orderInfo,
										items: itemsForCheckout,
										handlingCharge: onlinePricingResponse.responseObject.deliveryHandlingCharges,
										shipToSiteUseId: selectedAddress.SITE_USE_ID
									})
								);

								if (itemsForCheckout.length) {
									browserHistory.push('/order/createOrder');
								}
							}
						})
					);
				},
				{
					searchContext: 'PNET',
					include: {
						availability: true
					},
					showLoader: true
				}
			);
		}
	};

	const partsNetSalesAgreements = [user.selectedPriceSource];

	const handleDialog = (dialog, show) => {
		if (dialog === 'SingleShipmentLimitDialog') {
			setShowSingleShipmentLimitDialog(show);
		}
	};

	const handleChangeItemNumberTextBox = (itemIndex, itemNumber) => {
		setItemsToAdd(additionalItems =>
			additionalItems.map((additionalItem, index) => {
				if (index === itemIndex) {
					const newProduct = { ...additionalItem, itemNumber };
					if (itemNumber === '') {
						newProduct.itemDetails = null;
					}
					return newProduct;
				}
				return additionalItem;
			})
		);
	};

	const handleRequestedQuantityChange = (itemIndex, value) => {

		value ===''? setrequestedQuantityErr(true) : setrequestedQuantityErr(false);
		if (value > 0) {
			const {
				selectedAccount: { ACCOUNTNUMBER: accountNumber },
				selectedAddress: selectedAddressString
			} = user;
			const selectedAddress = JSON.parse(selectedAddressString || '{}');
			const zipCode = selectedAddress.COUNTRY === 'US' ? selectedAddress.POSTALCODE && selectedAddress.POSTALCODE.split('-')[0] : INTERNATIONAL_ZIP_CODE;
			let cartPayload = cart.cart.responseObject;
			const cartItems = cartPayload.cartItems.map((cartItem, index) => (index === itemIndex ? { ...cartItem, quantity: value } : cartItem));
			const cartItem = cartItems[itemIndex];

			if (shipSet !== '1') {
				const availabilityPayload = [
					{
						id: cartItem.buyerLineNo,
						orderLineIdentifier: 1,
						quantityOrdered: +value,
						requestDate: moment(cartPayload.requestedShipDate, DATE_FORMAT).format('YYYY-MM-DD'),
						shipSetName: cartPayload.shipSet || undefined,
						zipCode,
						accountNumber,
						inventoryItemName: cartItem.item,
						itemType: 'PARTS_ITEM'
					}
				];

				props.checkAvailabilityInBatches(availabilityPayload, PNET_APP_QUERY_PARAM, false, null, '', true, availabilityResponse => {
					const availabilityDataForItem =
						availabilityResponse.responseObject &&
						availabilityResponse.responseObject.find(
							availabilityItem => availabilityItem.id === cartItem.buyerLineNo && availabilityItem.inventoryItemName === availabilityPayload[0].inventoryItemName
						);
					dispatch(
						saveAvailabilityToCart({
							...availabilityResponse,
							responseObject: cart.availability.map(cartAvailabilityItem =>
								cartAvailabilityItem.id === cartItem.buyerLineNo ? availabilityDataForItem : cartAvailabilityItem
							)
						})
					);
				});
			} else {
				staleAvailabilityDataRef.current = true;
			}

			cartPayload = {
				...cartPayload,
				cartItems
			};

			updateCart(cartPayload);
		}
	};

	const handleCustomerReferenceChange = (itemIndex, value) => {
		let cartPayload = cart.cart.responseObject;
		const cartItems = cartPayload.cartItems.map((cartItem, index) => (index === itemIndex ? { ...cartItem, poNumber: value } : cartItem));

		cartPayload = {
			...cartPayload,
			cartItems
		};

		updateCart(cartPayload);
	};

	const addItemsToCart = (items, callback) => {
		let cartPayload = cart.cart.responseObject;

		const cartItems = items.map(item => ({
			...item,
			quantity: item.quantity || 1,
			createdDate: moment().format(DATE_TIME_FORMAT),
			updatedDate: moment().format(DATE_TIME_FORMAT)
		}));

		cartPayload = {
			...cartPayload,
			cartItems
		};

		if (shipSet === '1') {
			staleAvailabilityDataRef.current = true;
		}

		updateCart(cartPayload, callback);
	};

	const appendCartItems = itemsToAdd => {
		const newCartItems = [...cart.cart.responseObject.cartItems];

		itemsToAdd.forEach(itemToAdd => {
			newCartItems.push({
				item: itemToAdd.itemNumber,
				itemDescription: itemToAdd.pimData.item_description,
				quantity: itemToAdd.requestedQuantity || 1,
				parentItem: itemToAdd.originalItemNumber ? itemToAdd.originalItemNumber : null,
				agreementNumber: itemToAdd.agreementNumber || partsNetSalesAgreements[0].AGREEMENT_NUMBER,
				poNumber: '',
				buyerLineNo: itemToAdd.id
			});
		});

		addItemsToCart(newCartItems, () => setItemsToAdd([]));
	};

	const updateQuickOrderState = (_itemIndex, itemDetails) => {
		if (Array.isArray(itemDetails) && itemDetails.length) {
			const cartPimData = JSON.parse(JSON.stringify(cart.cartPimData ? cart.cartPimData.pimData : [])) || [];
			const cartOrderabilityData = cart.orderability ? [...cart.orderability] : [];
			const cartAvailabilityData = cart.availability ? [...cart.availability] : [];
			const cartPricingData = [];

			itemDetails.forEach(item => {
				const itemOrderabilityData = item.orderabilityData;
				const itemAvailabilityData = item.availabilityData;
				const itemPricingData = item.pricingData;
				const itemPimData = item.pimData;

				if (itemPimData && !cartPimData.find(pimItem => pimItem.item_number === item.itemNumber)) {
					cartPimData.push(itemPimData);
				}

				if (itemOrderabilityData && !cartOrderabilityData.some(orderabilityItem => orderabilityItem.INVENTORY_ITEM === item.itemNumber)) {
					cartOrderabilityData.push(itemOrderabilityData);
				}

				if (itemAvailabilityData) {
					cartAvailabilityData.push(itemAvailabilityData);
				}

				if (itemPricingData) {
					if (!cartPricingData.find(pricingItem => pricingItem.item_number === item.itemNumber)) {
						cartPricingData.push(itemPricingData);
					}
				}
			});

			dispatch(updateCartOrderPoints({ pimData: cartPimData }));
			dispatch(saveOrderabilityToCart({ responseObject: cartOrderabilityData }));
			dispatch(saveAvailabilityToCart({ responseObject: cartAvailabilityData }));
			if (cartPricingData.length) {
				dispatch(savePricesToCart({ responseObject: { items: cartPricingData } }, true));
			}

			appendCartItems(itemDetails);
		}
	};

	const handleRemoveAllCartItems = event => {
		event.preventDefault();
		const cartPayload = {
			cartItems: []
		};

		updateCart(cartPayload, () => {
			if (showFreightForwarder === 'Y') {
				setShowFreightForwarder('N');
			}
			dispatch(saveOrderabilityToCart(null));
			dispatch(resetAvailabilityToCart());
			dispatch(resetPricesToCart());
			dispatch(updateCartOrderPoints(null));
		});
	};

	const handleRemoveCartItem = (itemIndex, event) => {
		event.preventDefault();
		let cartPayload = cart.cart.responseObject;
		const cartItems = cartPayload.cartItems.filter((_cartItem, index) => {
			return index !== itemIndex;
		});

		if (cartItems.length) {
			cartPayload = {
				...cartPayload,
				cartItems
			};

			if (shipSet === '1') {
				staleAvailabilityDataRef.current = true;
			}
		} else {
			cartPayload = {
				cartItems
			};
		}

		updateCart(cartPayload, () => {
			if (!cartItems.length) {
				if (showFreightForwarder === 'Y') {
					setShowFreightForwarder('N');
				}
				dispatch(resetAvailabilityToCart());
				dispatch(resetPricesToCart());
				dispatch(updateCartOrderPoints(null));
			}
		});
	};

	const handleMoreLines = event => {
		event.preventDefault();

		if (shipSet === '1' && cart.cart && cart.cart.responseObject && cart.cart.responseObject.cartItems && cart.cart.responseObject.cartItems.length >= PNET_GOP_BATCH_SIZE) {
			handleDialog('SingleShipmentLimitDialog', true);
			return;
		}

		setItemsToAdd(additionalItems => [...additionalItems, { id: uuidv4(), itemNumber: '', quantity: 1, availability: null, error: null }]);
	};

	const handleRemoveNewItem = (itemIndex, event) => {
		event.preventDefault();
		setItemsToAdd(additionalItems => additionalItems.filter((additionalItem, index) => index !== itemIndex));
	};

	const handlePriceSourceChange = (_itemIndex, _event) => {};

	const verifyCartItems = event => {
		event && event.preventDefault();
		apiCallsRef.current.isAvailabilityCallCompleted = false;
		apiCallsRef.current.isPricingCallCompleted = false;
		apiCallsRef.current.isPimCallCompleted = false;

		const {
			selectedPriceSource: { AGREEMENT_NUMBER: agreementNumber }
		} = user;

		if (
			showFreightForwarder === 'Y' &&
			order.createOrderPayload &&
			order.createOrderPayload.availableFreightForwarders &&
			order.createOrderPayload.availableFreightForwarders.length &&
			(!cart.cart.responseObject.freightForwarder ||
				!order.createOrderPayload.availableFreightForwarders.find(
					availableFreightForwarder => availableFreightForwarder.SITE_USE_ID.toString() === cart.cart.responseObject.freightForwarder.split('|')[0]
				))
		) {
			setIsFreightForwarderError(true);
			scrollTo && scrollTo({ top: 0, behavior: 'auto' });
			return;
		}

		const isError =
			cart.cart.responseObject &&
			cart.cart.responseObject.cartItems.some(cartItem => {
				const itemOrderabilityData = orderability && orderability.find(orderabilityItem => orderabilityItem.INVENTORY_ITEM === cartItem.item);
				const itemAvailabilityData = availability && availability.find(availabilityItem => availabilityItem.inventoryItemName === cartItem.item);
				const itemPriceData = prices.find(
					priceItem =>
						priceItem &&
						priceItem.item_number === cartItem.item &&
						priceItem.records &&
						priceItem.records.length &&
						compareSalesAgreements(priceItem.records[0], cartItem) &&
						priceItem.records[0].unitPrice
				);
				const itemPimData = pimData && pimData.find(pimItem => pimItem.item_number === cartItem.item);
				const isPOKInstall = isPOKEnabled && itemPimData && itemPimData.geego_pok_install === 'Y';
				const pokOrderQuantity = itemPimData && itemPimData.geego_pok_ord_qty;

				if (
					!cartItem.quantity ||
					cartItem.quantity <= 0 ||
					requestedQuantityErr ||
					cartItem.agreementNumber !== agreementNumber.toString() ||
					!itemOrderabilityData ||
					itemOrderabilityData.ITEM_ORDERABILITY_FLAG !== 'Y' ||
					!itemAvailabilityData ||
					[80, 52].includes(itemAvailabilityData.errorCode) ||
					!itemAvailabilityData.sourceOrganizationID ||
					!itemPriceData ||
					!itemPimData ||
					itemPimData.item_status === 'Inactive' ||
					(isPOKEnabled && isPOKInstall && cartItem.quantity % pokOrderQuantity !== 0)
				) {
					// console.log('ITEM ERROR: ', cartItem, itemAvailabilityData, itemPriceData, itemPimData);
					return true;
				}
				return false;
			});

		if (isError) {
			return;
		}

		if (shipSet === '1' && cart.cart.responseObject && cart.cart.responseObject.cartItems.length > PNET_GOP_BATCH_SIZE) {
			handleDialog('SingleShipmentLimitDialog', true);
			return;
		}

		props.checkIfAnyActiveSoldAddress(() => {
			handleCreateOrder();
		});
	};

	const cartTotal = useMemo(
		() =>
			cartData &&
			cartData.responseObject &&
			cartData.responseObject.cartItems &&
			cartData.responseObject.cartItems.reduce((acc, cartItem) => {
				const itemPriceData = prices.find(
					priceItem =>
						priceItem &&
						priceItem.item_number === cartItem.item &&
						priceItem.records &&
						priceItem.records.length &&
						compareSalesAgreements(priceItem.records[0], cartItem)
				);
				let adjustedUnitPrice = 0;
				if (
					itemPriceData &&
					itemPriceData.records &&
					itemPriceData.records.length &&
					compareSalesAgreements(itemPriceData.records[0], cartItem) &&
					itemPriceData.records[0].unitPrice
				) {
					adjustedUnitPrice = itemPriceData.records[0].adjustedUnitPrice || 0;
				}

				return acc + cartItem.quantity * adjustedUnitPrice;
			}, 0),
		[cartData, pricingData]
	);

	const cartTotalForPayload = useMemo(
		() =>
			cartData &&
			cartData.responseObject &&
			cartData.responseObject.cartItems &&
			cartData.responseObject.cartItems.reduce((acc, cartItem) => {
				const itemPriceData = prices.find(
					priceItem =>
						priceItem &&
						priceItem.item_number === cartItem.item &&
						priceItem.records &&
						priceItem.records.length &&
						compareSalesAgreements(priceItem.records[0], cartItem)
				);
				let unitPrice = 0;
				if (
					itemPriceData &&
					itemPriceData.records &&
					itemPriceData.records.length &&
					compareSalesAgreements(itemPriceData.records[0], cartItem) &&
					itemPriceData.records[0].unitPrice
				) {
					unitPrice = itemPriceData.records[0].unitPrice || 0;
				}

				return acc + cartItem.quantity * unitPrice;
			}, 0),
		[cartData, pricingData]
	);

	return (
		<div className="pNetCart">
			<div>
				<h1 className="pageTitle">
					Cart <span className="unbold">(Create Order)</span>
				</h1>
			</div>
			{cart.cart && cart.cart.responseObject && cart.cart.responseObject.cartItems && cart.cart.responseObject.cartItems.length > 0 ? (
				<form className="pNetCartForm">
					{props.isInternationalPartsCustomer && (
						<div className="pNetQOOrderInfoRow">
							<div>
								<label htmlFor="pNetShowFreightForwarder">
									<b>Send Order to Freight Forwarder?</b>
								</label>
								<select
									id="pNetShowFreightForwarder"
									name="showFreightForwarder"
									className="fullWidth"
									value={showFreightForwarder}
									onChange={handleOrderDetailsChange}
								>
									<option value="Y">Yes</option>
									<option value="N">No</option>
								</select>
							</div>
							{showFreightForwarder === 'Y' && (
								<div className={isFreightForwarderError ? 'error' : undefined} style={{ width: 'min(100%, 25rem)', position: 'relative' }}>
									<label htmlFor="pNetFreightForwarder">
										<b>Freight Forwarder</b>
									</label>
									<select
										id="pNetFreightForwarder"
										className="fullWidth"
										name="freightForwarder"
										value={freightForwarderString ? freightForwarderString.split('|')[0] : ''}
										onChange={handleOrderDetailsChange}
									>
										<option value="">Select</option>
										{order.createOrderPayload &&
											order.createOrderPayload.availableFreightForwarders &&
											order.createOrderPayload.availableFreightForwarders.map(availableFreightForwarder => (
												<option key={availableFreightForwarder.SITE_USE_ID} value={availableFreightForwarder.SITE_USE_ID}>
													{[
														availableFreightForwarder.NAME,
														availableFreightForwarder.ADDRESS1,
														availableFreightForwarder.ADDRESS2,
														availableFreightForwarder.ADDRESS3,
														availableFreightForwarder.CITY,
														availableFreightForwarder.STATE,
														availableFreightForwarder.COUNTRY,
														availableFreightForwarder.POSTALCODE
													]
														.filter(addressField => addressField && addressField.trim())
														.join(', ')}
												</option>
											))}
									</select>
									{isFreightForwarderError && (
										<p className="message" style={{ position: 'absolute' }}>
											Please select a freight forwarder
										</p>
									)}
								</div>
							)}
						</div>
					)}
					<div className="pNetQOOrderInfoRow">
						<div>
							<label htmlFor="pNetOrderType">
								<b>Order Type</b>
							</label>
							<select id="pNetOrderType" className="fullWidth" name="orderType" value={orderType} onChange={handleOrderDetailsChange}>
								<option value="R">Regular</option>
								<option value="E">Emergency</option>
							</select>
						</div>
						<div>
							<label htmlFor="pNetReqShipDate">
								<b>Requested Ship Date</b>
							</label>
							<DatePicker
								id="pNetReqShipDate"
								className="pNetReqShipDate"
								name="requestedShipDate"
								filterDate={() => true}
								disabled={false}
								minDate={moment()}
								placeholderText="Click to select a date"
								selected={moment(requestedShipDate)}
								onChange={handleRequestedShipDateChange}
								style={{ width: '25%' }}
							/>
						</div>
					</div>
					<div className="pNetQOOrderInfoRow">
						<div>
							<label htmlFor="pNetShippingMethod">
								<b>Ship Method</b>
							</label>
							<select id="pNetShippingMethod" className="fullWidth" name="shipMethod" value={shipMethod} onChange={handleOrderDetailsChange}>
								{shippingMethods[props.isInternationalPartsCustomer ? 'international' : 'domestic'][orderType === 'R' ? 'regular' : 'emergency'].map(
									shippingMethodData => (
										<option key={shippingMethodData.displayName} value={shippingMethodData.displayName}>
											{shippingMethodData.displayName}
										</option>
									)
								)}
							</select>
						</div>
						<div>
							<label htmlFor="pNetShipSet">
								<b>Shipment Profile / Ship Set</b>
							</label>
							<select id="pNetShipSet" className="fullWidth" name="shipSet" value={shipSet || ''} onChange={handleOrderDetailsChange}>
								<option value="1">Single Shipment</option>
								<option value="">Unlimited Shipment</option>
							</select>
						</div>
					</div>
					<div className="pNetCartTableWrapper">
						<table className="pNetCartTable dataTable cartTable borderTable">
							<thead>
								<tr>
									<th className="actions textCenter">
										<a className="deleteAllLink" onClick={handleRemoveAllCartItems.bind(this)} href="#">
											Remove All
										</a>
									</th>
									<th>Item</th>
									<th className="subtable">
										<table>
											<thead>
												<tr>
													<th className="quote">Sales Agreement</th>
													<th className="price">
														Book
														<br />
														Price
													</th>
													<th className="price">
														Selling
														<br />
														Price
													</th>
													<th className="pNetItemReqQty text-center">
														Requested
														<br />
														Qty
													</th>
													<th className="extendedPrice text-center">
														Extended
														<br />
														Price
													</th>
													<th className="customerReference">
														Customer
														<br />
														Reference
													</th>
												</tr>
											</thead>
										</table>
									</th>
								</tr>
							</thead>
							<tbody>
								{cart.cart.responseObject.cartItems.map((cartItem, index) => {
									const itemOrderabilityData = orderability && orderability.find(orderabilityItem => orderabilityItem.INVENTORY_ITEM === cartItem.item);
									const itemAvailabilityData =
										availability &&
										availability.find(availabilityItem => availabilityItem.id === cartItem.buyerLineNo && availabilityItem.inventoryItemName === cartItem.item);
									let itemPriceData = prices.find(
										priceItem =>
											priceItem &&
											priceItem.item_number === cartItem.item &&
											priceItem.records &&
											priceItem.records.length &&
											compareSalesAgreements(priceItem.records[0], cartItem)
									);

									let unitPrice = 0,
										adjustedUnitPrice = 0;
									let isPriceAvailable = false;
									if (
										itemPriceData &&
										itemPriceData.records &&
										itemPriceData.records.length &&
										compareSalesAgreements(itemPriceData.records[0], cartItem) &&
										itemPriceData.records[0].unitPrice
									) {
										unitPrice = itemPriceData.records[0].unitPrice;
										adjustedUnitPrice = itemPriceData.records[0].adjustedUnitPrice || 0;
										isPriceAvailable = true;
									}

									const extendedPrice = adjustedUnitPrice * cartItem.quantity;
									const isQuantityNotAvailableOnRequestedDate = itemAvailabilityData && itemAvailabilityData.availableQuantity >= 1410065408;
									const itemData = itemAvailabilityData
										? {
												availableQuantity: itemAvailabilityData.availableQuantity,
												warehouseCode: itemAvailabilityData.sourceOrganizationCode && itemAvailabilityData.sourceOrganizationCode.split(':').at(-1),
												eta: itemAvailabilityData.arrivalDate,
												availabilityMessage:
													(isQuantityNotAvailableOnRequestedDate && 'BACKORDER') ||
													(itemAvailabilityData.availableQuantity && itemAvailabilityData.availableQuantity < cartItem.quantity && 'PARTIAL') ||
													'',
												errorCode: itemAvailabilityData.errorCode
										  }
										: {};

									const itemPimData = pimData && pimData.find(pimItem => pimItem.item_number === cartItem.item);
									const isPOKInstall = isPOKEnabled && itemPimData && itemPimData.geego_pok_install === 'Y';
									const pokOrderQuantity = itemPimData && itemPimData.geego_pok_ord_qty;
									let lifecyclePhase = itemPimData && itemPimData.lifecycle_phase;

									return (
										<tr key={cartItem.buyerLineNo} style={{ verticalAlign: 'top' }}>
											<td className="actions">
												<a className="removeProductLink" href="" onClick={handleRemoveCartItem.bind(this, index)}>
													✗ <u>Remove</u>
												</a>
											</td>
											<td>
												<div className="skuDiv" style={{ color: '#1a63a2' }}>
													{cartItem.item}
												</div>
												<p>{cartItem.itemDescription}</p>
												{cartItem.parentItem && (
													<p style={{ color: '#7a7a7a' }}>
														Supersedes <strong style={{ color: '#1a63a2' }}>{cartItem.parentItem}</strong>
													</p>
												)}
											</td>
											<td className="subtable">
												<table>
													<tbody>
														<tr className="text-center" style={{ verticalAlign: 'top' }}>
															<td className="quote text-left">
																<select value={cartItem.agreementNumber} onChange={handlePriceSourceChange.bind(this, index)}>
																	{!cartItem.agreementNumber && <option value="">-- Select --</option>}
																	{partsNetSalesAgreements.map(({ AGREEMENT_NUMBER, AGREEMENT_NAME }) => (
																		<option key={AGREEMENT_NUMBER} value={AGREEMENT_NUMBER}>
																			{AGREEMENT_NAME}
																		</option>
																	))}
																</select>
															</td>
															<td className="price">{props.formatMoney(parseFloat(unitPrice).toFixed(2), 0, '.', ',', false, '$')}</td>
															<td className="price">{props.formatMoney(parseFloat(adjustedUnitPrice).toFixed(2), 0, '.', ',', false, '$')}</td>
															<td className="pNetItemReqQty">
																<QuantityInput
																	className={
																		!cartItem.quantity ||
																		requestedQuantityErr === true ||
																		(isPOKInstall && cartItem.quantity % pokOrderQuantity !== 0) ||
																		(!itemData.availableQuantity && itemData.errorCode === 52)
																			? 'error'
																			: ''
																	}
																	value={cartItem.quantity}
																	onChange={handleRequestedQuantityChange.bind(this, index)}
																	disabled={
																		!itemOrderabilityData ||
																		itemOrderabilityData.error ||
																		!itemAvailabilityData ||
																		!itemAvailabilityData.sourceOrganizationID
																	}
																/>
															</td>
															<td className="extendedPrice">{props.formatMoney(parseFloat(extendedPrice).toFixed(2), 0, '.', ',', false, '$')}</td>
															<td className="customerReference">
																<CustomerReferenceInput
																	value={cartItem.poNumber || ''}
																	onChange={handleCustomerReferenceChange.bind(this, index)}
																/>
															</td>
														</tr>
														<tr>
															<td colSpan={3} className="productStatusTablePositioningContainer">
																<table
																	className="productStatusTable"
																	style={{
																		float: 'right',
																		marginRight: '15px',
																		width: '60%'
																	}}
																>
																	<thead>
																		<tr>
																			<th className="text-center" style={{ width: '30%' }}>
																				Available Qty
																			</th>
																			<th className="text-center">ETA</th>
																			<th className="text-center">Whse</th>
																			{isPOKInstall && <th className="text-center">Box Qty</th>}
																		</tr>
																	</thead>
																	<tbody>
																		<tr style={{ verticalAlign: 'top' }}>
																			<td
																				className={`text-center bold${
																					isQuantityNotAvailableOnRequestedDate && !itemData.eta ? ' red' : ''
																				}`}
																				style={{ width: '30%' }}
																			>
																				<React.Fragment>
																					{isQuantityNotAvailableOnRequestedDate ? 0 : itemData.availableQuantity}
																					{itemData.availabilityMessage && (
																						<strong
																							className="block"
																							style={{ fontSize: '.7em', marginTop: '.25rem', color: '#7a7a7a' }}
																						>
																							{itemData.availabilityMessage}
																						</strong>
																					)}
																				</React.Fragment>
																			</td>
																			<td className="text-center">
																				{isQuantityNotAvailableOnRequestedDate && itemData.eta && moment(itemData.eta).format(DATE_FORMAT)}
																			</td>
																			<td className="text-center">{itemData.warehouseCode}</td>
																			{isPOKInstall && <td className="text-center">{itemPimData.geego_pok_ord_qty}</td>}
																		</tr>
																	</tbody>
																</table>
															</td>
														</tr>
													</tbody>
												</table>
												{lifecyclePhase === 'NLA' && <p className="red">This item is no longer available.</p>}
												{(!itemOrderabilityData ||
													(!itemOrderabilityData.error && itemOrderabilityData.ITEM_ORDERABILITY_FLAG !== 'Y') ||
													!itemAvailabilityData) && <p className="red">This item cannot be ordered.</p>}
												{((itemOrderabilityData && itemOrderabilityData.error) ||
													(itemAvailabilityData &&
														!itemAvailabilityData.sourceOrganizationID &&
														itemAvailabilityData.sourceOrganizationCode &&
														!itemAvailabilityData.sourceOrganizationCode.includes(':'))) && (
													<p className="red">An error has occured, please try again shortly.</p>
												)}
												{itemAvailabilityData &&
													!itemAvailabilityData.sourceOrganizationID &&
													itemAvailabilityData.sourceOrganizationCode &&
													itemAvailabilityData.sourceOrganizationCode.includes(':') && (
														<p className="red">This item cannot be ordered from this warehouse.</p>
													)}
												{hasPricingAccess && isPriceAvailable === false && <p className="red">This item is not priced.</p>}
												{isPOKEnabled && isPOKInstall && cartItem.quantity % pokOrderQuantity !== 0 && (
													<p className="red">Quantity must be in multiples of Box Quantity.</p>
												)}
											</td>
										</tr>
									);
								})}
								{itemsToAdd.map((itemToAdd, index) => {
									return (
										<tr key={index} className="text-center">
											<td className="actions">
												<a className="removeProductLink" href="" onClick={handleRemoveNewItem.bind(this, index)}>
													✗ <u>Remove</u>
												</a>
											</td>
											<td>
												<AccountNumberAutoSuggests
													checkAvailabilityAndOrderability={props.checkAvailabilityAndOrderability}
													getShippingAddressesDetails={props.getShippingAddressesDetails}
													width="100%"
													size={8}
													value={itemToAdd.itemNumber}
													updateQuickOrderState={updateQuickOrderState}
													onChangeProductNumberTextBox={handleChangeItemNumberTextBox}
													itemIndex={index}
													itemDetails={itemToAdd}
													getSalesAgreementParamForAvailability={props.getSalesAgreementParamForAvailability}
													isPart
													requestedShipDate={moment(requestedShipDate)}
													shipSet={shipSet}
													quantity={1}
													searchContext="PNET"
													getItemDetails={props.getItemDetails}
													skipInvalidItems
												/>
												{(itemToAdd.itemDetails && itemToAdd.itemDetails.status === false) || itemToAdd.itemDetails ? (
													<span className="quickOrderTableRowError">You must enter a valid full or partial product number.</span>
												) : null}
											</td>
											<td></td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						{true && (
							<a className="bold noUnderline uppercase" href="" id="addQuickOrderTableRow" onClick={handleMoreLines}>
								+ <u>ADD MORE</u>
							</a>
						)}
						<table className="subtotalTable cartSubtotalTable">
							<tbody>
								<tr>
									<td style={{ textAlign: 'right' }}>Subtotal:</td>
									<td className="price" style={{ textAlign: 'center' }}>
										&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {props.formatMoney(parseFloat(cartTotal).toFixed(2), 0, '.', ',', false, '$')}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<p className="textRight">
						{hasPricingAccess && hasCreateOrderAccess && (
							<a className="greenButton" href="" onClick={verifyCartItems}>
								Create Order ►
							</a>
						)}
					</p>
				</form>
			) : (
				<p>There are currently no products in your Cart.</p>
			)}
			{showSingleShipmentLimitDialog && (
				<GenericOverlayDialog
					buttons="ok"
					genericDialogHeading={`Item Limit ${cartItems && cartItems.length === PNET_GOP_BATCH_SIZE ? 'Reached' : 'Exceeded'}`}
					genericDialogText={`You can add at most ${PNET_GOP_BATCH_SIZE} items for Single Shipment.${
						cartItems && cartItems.length === PNET_GOP_BATCH_SIZE ? '' : ' Please remove and resubmit.'
					}`}
					flag="SingleShipmentLimitDialog"
					handleOk={handleDialog.bind(this, 'SingleShipmentLimitDialog', false)}
					showDialog={true}
					defaultTop
				/>
			)}
		</div>
	);
};

export default PartsNetCart;



// WEBPACK FOOTER //
// ./src/App/components/PostLogin/Cart/partsNetCart.jsx